import { useBreakpointValue } from '@genial/utils';
import React from 'react';

import { AssetSelectedTypeMap } from '../../../../../../../store/product/types';
import { useEquityAddedProductItem } from '../../../../../hooks';
import {
  AddedProductItemList,
  AddedProductItemRouteParamTypeProps,
  AssetWithErrorResponse,
} from '../../../../../utils/types';
import { AddedProductItemContainer } from '../Container';

import { AddedProductItemEquityDesktop } from './desktop';
import { AddedProductItemEquityMobile } from './mobile';
import { AddedProductItemEquitySkeleton } from './skeleton';
import { AddedProductItemEquityTablet } from './tablet';

type EquityData = AssetSelectedTypeMap['equity'] & AssetWithErrorResponse;

export interface AddedProductItemEquityProps {
  data: EquityData;
  type: AddedProductItemRouteParamTypeProps;
  isInvalid?: boolean;
  orderIsRecused?: boolean;
}

export interface AddedProductResponsiveItemEquityProps {
  data: EquityData;
  onRemoveProduct: () => void;
  priceChangeTaxIconColor: string;
  priceChangeTaxTextColor: string;
  hasPriceChangeTax: boolean;
  priceChangeTaxIcon: 'ArrowDown' | 'ArrowUp';
  onChangePriceItem: (value: number) => void;
  onChangeQuantityItem: (value: number) => void;
  totalPriceIsInvalid: boolean;
  totalPrice: number;
  priceItem: number;
  quantityItem: number;
  lateralBarBackgroundColor: string;
  inputIsDisabled: boolean;
}

export const AddedProductItemEquity: React.FC<AddedProductItemEquityProps> = ({
  data,
  type,
  isInvalid,
  orderIsRecused,
}) => {
  const {
    onRemoveProduct,
    priceChangeTaxIconColor,
    priceChangeTaxTextColor,
    hasPriceChangeTax,
    priceChangeTaxIcon,
    onChangePriceItem,
    onChangeQuantityItem,
    priceItem,
    quantityItem,
    totalPrice,
    isLoadingAction,
    totalPriceIsInvalid,
    inputIsDisabled,
  } = useEquityAddedProductItem({
    data,
    type,
  });

  const lateralBarBackgroundColor = isInvalid
    ? 'feedback.attention.lightest'
    : 'product.rendavariavel';

  const params = {
    data,
    lateralBarBackgroundColor,
    totalPriceIsInvalid,
    onRemoveProduct,
    priceChangeTaxIconColor,
    priceChangeTaxTextColor,
    hasPriceChangeTax,
    priceChangeTaxIcon,
    onChangePriceItem,
    onChangeQuantityItem,
    priceItem,
    quantityItem,
    totalPrice,
    inputIsDisabled,
  };

  const layout = useBreakpointValue({
    base: <AddedProductItemEquityMobile {...params} />,
    sm: <AddedProductItemEquityTablet {...params} />,
    lg: <AddedProductItemEquityDesktop {...params} />,
  });

  if (isLoadingAction) {
    return <AddedProductItemEquitySkeleton />;
  }

  return (
    <AddedProductItemContainer
      data={data as AddedProductItemList}
      isInvalid={!!isInvalid}
      orderIsRecused={!!orderIsRecused}
      key={data.productIdNormalized}
      lateralBarBackgroundColor={lateralBarBackgroundColor}>
      {layout}
    </AddedProductItemContainer>
  );
};
